import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Star } from 'lucide-react';

const testimonials = [
  {
    name: 'Sarah Johnson',
    role: 'Graphic Designer',
    image: '/pexels-photo-1181686.webp',
    text: 'WinMax Optimizer transformed my aging laptop into a powerhouse. My design software now runs without any lag or stuttering. Absolutely worth every penny!',
    rating: 5
  },
  {
    name: 'Michael Chen',
    role: 'Software Developer',
    image: '/pexels-photo-220453.webp',
    text: 'As a developer, I need my system running at peak performance. WinMax Optimizer has significantly improved my build times and overall system responsiveness.',
    rating: 5
  },
  {
    name: 'Emily Parker',
    role: 'Video Editor',
    image: '/pexels-photo-1239291.webp',
    text: 'I edit 4K video for a living and my PC was struggling. After running WinMax Optimizer, rendering times decreased by almost 40%. Amazing results!',
    rating: 5
  },
  {
    name: 'David Wilson',
    role: 'IT Manager',
    image: '/pexels-photo-2379004.webp',
    text: 'We deployed WinMax Optimizer across our entire office, and the difference is remarkable. Our support tickets for slow computers dropped by 70%.',
    rating: 4
  }
];

const Testimonials: React.FC = () => {
  const [current, setCurrent] = useState(0);

  const next = () => {
    setCurrent((current + 1) % testimonials.length);
  };

  const prev = () => {
    setCurrent((current - 1 + testimonials.length) % testimonials.length);
  };

  const goToSlide = (index: number) => {
    setCurrent(index);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') {
      prev();
    } else if (e.key === 'ArrowRight') {
      next();
    }
  };

  return (
    <section
      id="testimonials"
      className="py-20 bg-dark-900"
      aria-label="Customer Testimonials"
    >
      <div className="container px-4 mx-auto">
        <div className="mb-16 text-center">
          <h2 className="mb-4 text-3xl font-bold text-white md:text-4xl">What Our Users Say</h2>
          <p className="mx-auto max-w-3xl text-xl text-dark-300">
            Don't just take our word for it. Here's what our customers have experienced with WinMax Optimizer.
          </p>
        </div>

        <div
          className="relative mx-auto max-w-5xl"
          role="region"
          aria-roledescription="carousel"
          aria-label="Customer testimonials carousel"
          onKeyDown={handleKeyDown}
          tabIndex={0}
        >
          <div className="overflow-hidden">
            <div
              className="flex transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(-${current * 100}%)` }}
              role="presentation"
            >
              {testimonials.map((testimonial, index) => (
                <div
                  key={index}
                  className="flex-shrink-0 p-4 w-full"
                  role="group"
                  aria-roledescription="slide"
                  aria-label={`${index + 1} of ${testimonials.length}`}
                  aria-hidden={current !== index}
                >
                  <motion.div
                    className="p-8 rounded-lg border shadow-xl bg-dark-800 border-dark-700"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                  >
                    <div className="flex flex-col items-start mb-6 md:flex-row md:items-center">
                      <img
                        loading="lazy"
                        decoding="async"
                        src={testimonial.image}
                        alt={`${testimonial.name}, ${testimonial.role}`}
                        className="object-cover mb-4 w-16 h-16 rounded-full md:mb-0 md:mr-4"
                      />
                      <div>
                        <h3 className="text-xl font-semibold text-white">{testimonial.name}</h3>
                        <p className="text-dark-400">{testimonial.role}</p>
                        <div
                          className="flex mt-1"
                          role="img"
                          aria-label={`Rating: ${testimonial.rating} out of 5 stars`}
                        >
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              size={16}
                              className={i < testimonial.rating ? "text-yellow-400 fill-yellow-400" : "text-dark-600"}
                              aria-hidden="true"
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                    <blockquote>
                      <p className="italic text-dark-300">{testimonial.text}</p>
                    </blockquote>
                  </motion.div>
                </div>
              ))}
            </div>
          </div>

          <button
            className="absolute left-0 top-1/2 z-10 p-4 rounded-full border shadow-xl -translate-x-4 -translate-y-1/2 bg-dark-800 hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:ring-offset-2 focus:ring-offset-dark-900 border-dark-700"
            onClick={prev}
            aria-label="Previous testimonial"
          >
            <ChevronLeft size={24} className="text-white" aria-hidden="true" />
          </button>

          <button
            className="absolute right-0 top-1/2 z-10 p-4 rounded-full border shadow-xl translate-x-4 -translate-y-1/2 bg-dark-800 hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:ring-offset-2 focus:ring-offset-dark-900 border-dark-700"
            onClick={next}
            aria-label="Next testimonial"
          >
            <ChevronRight size={24} className="text-white" aria-hidden="true" />
          </button>

          <div
            className="flex justify-center mt-6 space-x-4"
            role="tablist"
            aria-label="Choose testimonial"
          >
            {testimonials.map((_, index) => (
              <button
                key={index}
                role="tab"
                aria-selected={current === index}
                aria-label={`Go to testimonial ${index + 1}`}
                onClick={() => goToSlide(index)}
                className={`w-4 h-4 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-400 focus:ring-offset-2 focus:ring-offset-dark-900 ${current === index ? 'bg-primary-400' : 'bg-dark-700'
                  }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;